import {
  FilterKeys,
  Filters,
  FilterTitle,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
  SelectedFilters,
} from '../pageConfig/Types';
import { PagesConfig } from '../pageConfig/PagesConfigContext';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import {
  clearAllFilters,
  exploreResponseConfig,
  FilterCarryOverScope,
  FilterConfig,
  getBaseFilterKeysByPageTypeAndPageName,
  getFilterSettingsByPage,
  multiCountryProductGroupConfigs,
  sortFilters,
} from '../FilterSettings';
import { cloneDeep, isEqual } from 'lodash';
import { checkIsRetailerSame, createToUpdateFilter, isOnlyBaseFiltersApplied } from '../FilterUtils';
import { getMergeAndUniqObjectArrayByKey } from '../ObjectUtils';
import {
  GLOBAL_FILTER_APPLIED_TRACKING_TITLE,
  globalFiltersTrackingFunction,
  GlobalFiltersTrackingFunctionParam,
} from '../page-analytics/model/GlobalFiltersTracking';
import { FilterName, FilterType } from '../page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { byFilterKey, excludeByFilterKey } from '../pageConfig/Predicates/SelectedFiltersPredicates';
import { ProductType } from '@pages/product-analyzer/Product';
import { getExchangeRate, isLocalCurrencyApplied } from '../currencyUtils/CurrencyUtils';
import { checkIsMultiCountryClient } from '../multiCountryUtils/MultiCountryUtils';
import { formatPriceBandAsPerExchangeRate } from '@utils/Formatters';

/*
 * Maximum number of filters allowed for the mutually exclusive checks in side filters
 * eg. Performance analyzer only allows max 2 filters between LOCATION, CHANNEL, PRICEBAND, BRANDS
 * Product analyzer only allows max 1 filter between REGION, CITY, ONLINE/OFFLINE, CHANNEL
 */
export enum MaxAllowedFilters {
  PerformanceAnalyzer = 2,
  ProductAnalyzer = 1,
}

export const removePricebandFilterByAppliedCurrency = (
  pagesConfig: PagesConfig,
  currentProductType: ProductType,
  appliedCountry: Filters,
  filtersToUpdate: SelectedFilters[],
) => {
  const currentSelectedFilters = filtersToUpdate[pagesConfig.pageType].selectedFilters;
  const currencyFilter = currentSelectedFilters.find(byFilterKey(FilterKeys.CURRENCY));
  const productGroupFilter = currentSelectedFilters.find(byFilterKey(FilterKeys.PRODUCT_GROUP));

  if (
    !currencyFilter ||
    (isLocalCurrencyApplied(pagesConfig, currentProductType, currencyFilter, appliedCountry) &&
      productGroupFilter?.options.length === 1)
  ) {
    return;
  }

  filtersToUpdate[pagesConfig.pageType].selectedFilters = currentSelectedFilters.filter(
    excludeByFilterKey(FilterKeys.PRICEBAND),
  );
};

/*
 * function that applies the selected filters and stores it in the context
 */
export const onFilterTrayApply = (
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
  retailerClientState: any,
  track: (event: string, properties?: Object) => void,
  currentProductType: ProductType,
) => {
  const { pageType: currentPageType } = pagesConfig;
  const selectedFilters = pagesConfig.selectedFilters[currentPageType].selectedFilters;
  const selectedFiltersCountry = [...selectedFilters].find(byFilterKey(FilterKeys.COUNTRY));

  const appliedFilters = pagesConfig.appliedFilters[currentPageType].selectedFilters;
  // There will always be an applied country as it is a base filter
  const appliedFiltersCountry = [...appliedFilters].find(byFilterKey(FilterKeys.COUNTRY))!;

  if (selectedFiltersCountry?.options[0].value !== appliedFiltersCountry?.options[0].value) {
    pagesConfig.setIsCountryChanged(true);
  }

  const newSelectedFilters = structuredClone(pagesConfig.selectedFilters);

  const { pageName, isMultiCountryFeatureFlagPA } = pagesConfig;
  //Removed OR from condition to make it alike explore/TA in terms of multi-currency
  if (isMultiCountryFeatureFlagPA && [PageName.PERFORMANCE_ANALYSIS].includes(pageName)) {
    removePricebandFilterByAppliedCurrency(pagesConfig, currentProductType, appliedFiltersCountry, newSelectedFilters);
  }

  const isMultiPg =
    newSelectedFilters[currentPageType].selectedFilters.find((f) => f.filterKey === FilterKeys.PRODUCT_GROUP)?.options
      .length! > 1;

  const excludedPagesForCarryOverFilters = getExcludedPagesToCarryOver({
    ...pagesConfig,
    appliedFilters: newSelectedFilters,
  });
  let carriedOverAllFilters = getCarryOverAllFiltersForApply(
    newSelectedFilters,
    currentPageType,
    undefined,
    excludedPagesForCarryOverFilters,
  );

  // Carry over logic specific for below included pgs
  if ([PageName.EXPLORE, PageName.OPPORTUNITY_REVIEW, PageName.TRADING_ASSISTANT].includes(pageName)) {
    carriedOverAllFilters = getCarryOverAllFiltersForApplyV2(carriedOverAllFilters, currentPageType);
  }

  removeMultiFeature(carriedOverAllFilters, isMultiPg);

  const currencyExchangeRate = getExchangeRate(pagesConfig);
  if (currencyExchangeRate > 0) {
    //considering all pages where priceband present (EXPLORE, OR, TA), if not like RO, PRODUCT this will get skipped at priceband?
    let priceband = carriedOverAllFilters[currentPageType].selectedFilters.find(
      (f) => f.filterKey === FilterKeys.PRICEBAND,
    );
    priceband?.options.forEach((option) => {
      option.label = formatPriceBandAsPerExchangeRate(option.value, currencyExchangeRate);
    });
  }

  pagesConfig.setSelectedFilters(carriedOverAllFilters);
  pagesConfig.setAppliedFilters(carriedOverAllFilters);
  pagesConfig.setApplyFilters(false);
  pagesConfig.setClearFilters(false);

  const storeFilterSettings = {
    appliedFilters: carriedOverAllFilters,
  };

  updateSideFilters(storeFilterSettings);

  //Start Global Filter Tracking
  const params: GlobalFiltersTrackingFunctionParam = {
    eventTitle: GLOBAL_FILTER_APPLIED_TRACKING_TITLE,
    sideFilters: storeFilterSettings.appliedFilters,
    retailerClientState: retailerClientState,
    track: track,
    pageType: pagesConfig.pageType,
    pageName: pagesConfig.pageName,
    filterName: FilterName.GLOBAL_FILTER,
    filterType: FilterType.PLATFORM,
    access: pagesConfig.access,
  };

  globalFiltersTrackingFunction(params);
  //End Global Filter Tracking

  // disables clear if pg overview only has the 2 base filters applied
  if (isOnlyBaseFiltersApplied(pagesConfig.getPageTypeAndName(), carriedOverAllFilters)) {
    pagesConfig.setClearFilters(false);
  }
};

/**
 * function that clears the selected filters in the filter tray and enables the apply button
 */
export const onFilterTrayClear = (pagesConfig: PagesConfig) => {
  const appliedFilter = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters;
  const allFilters = clearAllFilters(pagesConfig.appliedFilters, pagesConfig.getPageTypeAndName());

  pagesConfig.setSelectedFilters(allFilters);
  pagesConfig.setIsClearAllFilterTriggered(true); //to prevent reload upon clearing filter tray
  const isApplyButtonDisabled = !isEqualWithCaseInsensitive(
    appliedFilter,
    allFilters[pagesConfig.pageType].selectedFilters,
  );
  pagesConfig.setApplyFilters(isApplyButtonDisabled);
  pagesConfig.setClearFilters(false);
};

/**
 * Returns a copy of all filters with the specified filter deleted.
 * @param allSelectedFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param page Specifies which page to remove filter
 * @param filterKey Specifies which filterKey to remove filter
 * @param toRemove Specifies which option to remove, if falsy, remove the filter
 * @param filterTitle
 * @returns copy of all filters with deleted filter
 */
const getRemovedSpecificFilter = (
  allSelectedFilters: SelectedFilters[],
  page: PageType,
  filterKey: FilterKeys,
  toRemove?: string,
  filterTitle?: string,
): SelectedFilters[] => {
  const allFilters = [...allSelectedFilters];
  if (!allFilters[page]) {
    return allFilters;
  }
  const filters = [...allFilters[page].selectedFilters];
  const filterIndex = filters.findIndex((e) => {
    if (toRemove && filterTitle) {
      return (
        e.filterKey === filterKey &&
        e.filterTitle === filterTitle &&
        e.options.find((value) => value.value === toRemove)
      );
    }
    return e.filterKey === filterKey;
  });

  if (filterIndex === -1) {
    return allFilters;
  }
  const options = filters[filterIndex].options;
  const optionsIndex = options.findIndex((e) => e.value === toRemove);
  if (optionsIndex > -1) {
    filters[filterIndex].options.splice(optionsIndex, 1);
  }
  if (filters[filterIndex].options.length === 0 || !toRemove) {
    filters.splice(filterIndex, 1);
  }
  allFilters[page] = { pageType: page, selectedFilters: filters };

  allFilters.forEach((pageFilters) => {
    pageFilters.selectedFilters = pageFilters.selectedFilters.filter((filter) => filter.options.length !== 0);
  });

  return allFilters;
};

/**
 * Returns specified filterSetting object, if found.
 * @param key Specifies filterKey to view filter setting
 * @param page Specifies page to view filter setting
 * @returns Specified filterSetting object, undefined if not found
 */
const getSpecificFilterSettings = (key: FilterKeys, page: PageType) => {
  const pageSettings = getFilterSettingsByPage(page);
  const settingsIndex = pageSettings.findIndex((e) => e.filterKey === key);
  if (settingsIndex > -1) {
    return {
      settingItem: pageSettings[settingsIndex],
    };
  }
  return {
    settingItem: undefined,
  };
};

/**
 * Returns pages to carry over for the specified filter based on filterSettings.
 * @param key Specifies filterKey to view filter setting
 * @param page Specifies page to view filter setting
 * @returns Specified filter carry over pages, empty array if not found
 */
const getCarryOverPagesByFilterSettings = (key: FilterKeys, page: PageType): PageType[] => {
  const { settingItem } = getSpecificFilterSettings(key, page);
  if (settingItem) {
    return settingItem.carryOverScope.getPageTypes();
  }
  return [];
};

/**
 * Returns a copy of carried over filters at specified page, for apply only.
 * @param allFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param currentPageFilter Specifies current page filter
 * @param pageToCarryOver Specifies page to carry over with current page filter
 * @returns Copy of carried over filters
 */
const getCarryOverSinglePageFiltersForApply = (
  allFilters: SelectedFilters[],
  currentPageFilter: Filters,
  pageToCarryOver: PageType,
): Filters[] => {
  const clonedAllFilters: SelectedFilters[] = cloneDeep(allFilters);
  // No existing filters at all then put in as new filters
  if (!clonedAllFilters[pageToCarryOver] || clonedAllFilters[pageToCarryOver].selectedFilters.length === 0) {
    return [currentPageFilter];
  }
  const carryOverPageFilters: Filters[] = clonedAllFilters[pageToCarryOver].selectedFilters;
  const matchingFilterKeyFilterIndex = carryOverPageFilters.findIndex(
    (carryOverPageFilter) =>
      carryOverPageFilter.filterKey === currentPageFilter.filterKey &&
      carryOverPageFilter.filterTitle === currentPageFilter.filterTitle,
  );
  // Has filters, matches filterKey, overwrite selectedFilter
  if (matchingFilterKeyFilterIndex > -1) {
    carryOverPageFilters[matchingFilterKeyFilterIndex] = currentPageFilter;
    return carryOverPageFilters;
  }
  // Has filters, does not match filterKey, add into selectedFilter
  carryOverPageFilters.push(currentPageFilter);
  return carryOverPageFilters;
};

/**
 * Returns a copy of carried over all filters for side menu apply operation.
 *
 * Includes:
 * 1. Apply at normal filter tray
 * 2. Apply at saved filter tray
 *
 * See: https://jira.gfk.com/browse/NEO-18829
 * @param allFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param currentPage Specifies current page
 * @param toRemoveBasedOnFilterSetting filter keys from settings
 * @param pagesToExclude Array of pages to be excluded for carry over filter
 * @returns Copy of carried over all filters
 */
export const getCarryOverAllFiltersForApply = (
  allFilters: SelectedFilters[],
  currentPage: PageType,
  toRemoveBasedOnFilterSetting?: FilterKeys[],
  pagesToExclude?: PageType[],
): SelectedFilters[] => {
  const clonedAllFilters = cloneDeep(allFilters);
  let carriedOverFilters: SelectedFilters[] = clonedAllFilters;
  const currentPageFilters: Filters[] = clonedAllFilters[currentPage].selectedFilters;
  const filterSettingsForCurrentPage = getFilterSettingsByPage(currentPage);
  filterSettingsForCurrentPage.forEach((currentPageSetting: FilterConfig) => {
    //we can add one more parameter here to exclude the pages to filter
    const defaultPagesToCarryOver: PageType[] = getCarryOverPagesByFilterSettings(
      currentPageSetting.filterKey,
      currentPage,
    );
    const filteredPagesToCarryOver = defaultPagesToCarryOver.filter(
      (defaultPage) => !pagesToExclude?.includes(defaultPage),
    );
    carriedOverFilters = updateCarriedOverFilters(
      currentPageFilters,
      filteredPagesToCarryOver,
      carriedOverFilters,
      currentPageSetting,
      toRemoveBasedOnFilterSetting,
    );
  });
  return carriedOverFilters;
};

const updateCarriedOverFilters = (
  currentPageFilters: Filters[],
  pagesToCarryOver: PageType[],
  carriedOverFilters: SelectedFilters[],
  currentPageSetting: any,
  toRemoveBasedOnFilterSetting?: FilterKeys[],
) => {
  const currentPageFilterFilterKeys: FilterKeys[] = currentPageFilters.map(
    (currentPageFilter) => currentPageFilter.filterKey,
  );
  currentPageFilters.forEach((currentPageFilter) => {
    const carriedOverAllFiltersForCurrentPageFilter: SelectedFilters[] = pagesToCarryOver.map((pageToCarryOver) => {
      //Added new condition to ignore Currency Filters, since we do not want the currency filter to be removed from
      //PA page when carrying over from OR to PA
      if (currentPageFilter.filterKey === FilterKeys.CURRENCY && pageToCarryOver === PageType.PERFORMANCE_ANALYSIS) {
        return carriedOverFilters[pageToCarryOver];
      }
      // that exact match current page filters, carry over filter to other pages
      if (currentPageSetting.filterKey === currentPageFilter.filterKey) {
        return {
          pageType: pageToCarryOver,
          selectedFilters: getCarryOverSinglePageFiltersForApply(
            carriedOverFilters,
            currentPageFilter,
            pageToCarryOver,
          ),
        };
      }
      // to remove any filter that does not match current page filters setting, delete filter for other pages
      if (toRemoveBasedOnFilterSetting?.includes(currentPageFilter.filterKey)) {
        return getRemovedSpecificFilter(carriedOverFilters, pageToCarryOver, currentPageFilter.filterKey)[
          pageToCarryOver
        ];
      }
      // that does not match any current page filters, delete filter for other pages
      if (!currentPageFilterFilterKeys.includes(currentPageSetting.filterKey)) {
        return getRemovedSpecificFilter(carriedOverFilters, pageToCarryOver, currentPageSetting.filterKey)[
          pageToCarryOver
        ];
      }
      // that match any current page filters, but not exact match current page filters, do nothing
      return carriedOverFilters[pageToCarryOver];
    });
    // merge carry over object array to apply changes to final result
    carriedOverFilters = getMergeAndUniqObjectArrayByKey(
      carriedOverFilters,
      carriedOverAllFiltersForCurrentPageFilter,
      'pageType',
    );
  });

  // If not dealing with ADDFEAT/SEGFEAT, return the carried-over filters as is
  if (![FilterKeys.ADDFEAT, FilterKeys.SEGFEAT].includes(currentPageSetting.filterKey)) {
    return carriedOverFilters;
  }

  // Clean up unselected features for ADDFEAT/SEGFEAT filters across shared pages
  return cleanUpSelectedItemFeatures({
    currentPageFilters,
    currentPageSetting,
    carriedOverFilters,
    pagesToCarryOver,
  });
};

// Helper to clean up unselected ADDFEAT/SEGFEAT features across pages
const cleanUpSelectedItemFeatures = ({
  currentPageFilters,
  currentPageSetting,
  carriedOverFilters,
  pagesToCarryOver,
}: {
  currentPageFilters: Filters[];
  currentPageSetting: FilterConfig;
  carriedOverFilters: SelectedFilters[];
  pagesToCarryOver: PageType[];
}) => {
  const newSelectedItemFeatures = currentPageFilters.filter(
    (value) => value.filterKey === currentPageSetting.filterKey,
  );

  const cleanedFilters = pagesToCarryOver.map((pageType) => {
    const clonedSelectedFiltersPage = cloneDeep(carriedOverFilters[pageType]);
    const filteredFeatures = clonedSelectedFiltersPage.selectedFilters.filter((feature) => {
      if (feature.filterKey === currentPageSetting.filterKey) {
        return newSelectedItemFeatures.some(
          (selected) => selected.filterKey === feature.filterKey && selected.filterTitle === feature.filterTitle,
        );
      }
      return true;
    });

    return { pageType, selectedFilters: filteredFeatures };
  });

  return getMergeAndUniqObjectArrayByKey(carriedOverFilters, cleanedFilters, 'pageType');
};

export const removeFilterByKey = (filters: Filters[], filterKey: FilterKeys) => {
  const filterIndex = filters.findIndex(byFilterKey(filterKey));
  if (filterIndex > -1) {
    filters.splice(filterIndex, 1);
  }
};

export const updateFilters = (filtersOfTargetPage: Filters[], currentPageFilter: Filters) => {
  const filterIndex = filtersOfTargetPage.findIndex(byFilterKey(currentPageFilter.filterKey));
  const copyOfCurrentPageFilter = cloneDeep(currentPageFilter);
  if (filterIndex > -1) {
    filtersOfTargetPage.splice(filterIndex, 1, copyOfCurrentPageFilter);
  } else {
    filtersOfTargetPage.push(copyOfCurrentPageFilter);
  }
};

export const shouldSkipMultiCountryFilter = (
  filterKey: FilterKeys,
  isMultiCountrySelected: boolean,
  pageToCarryOver: PageType,
  multiPgPageTypes: PageType[],
  accFinalFilters: SelectedFilters[],
): boolean => {
  if (filterKey === FilterKeys.COUNTRY) {
    // Multi Country
    // RO: Don't bring over anything, i.e. if page is multi pg, don't bring over
    if (multiPgPageTypes.includes(pageToCarryOver) && isMultiCountrySelected) {
      return true;
    }

    // OR: If first load, popup modal (i.e. no pg filter)
    const currentPageAppliedCountries = accFinalFilters[pageToCarryOver].selectedFilters.find(byFilterKey(filterKey));
    if (currentPageAppliedCountries === undefined && isMultiCountrySelected) {
      return true;
    }

    // For now, we cannot carry over multi country selection over to PA, since there
    // isn't a way to differentiate between OR and PA page when applying a filter because
    // they share the same pageType (PageType.OPPORTUNITY_REVIEW).
    // However, once we seperate each side filter to its independent pageTypes, only then
    // may we be able to carry over multi country selection to PA.

    // OR: If not first load, retain previous selections, aka do nothing
    // PA: Should PA carry over multi country selection
    if ((pageToCarryOver === PageType.OPPORTUNITY_REVIEW || pageToCarryOver === PageType.TRADING_ASSISTANT) && isMultiCountrySelected) {
      return true;
    }
  }
  return false;
};

export const shouldRemoveMultiPgFilter = (
  filterKey: FilterKeys,
  isMultiPg: boolean,
  pageToCarryOver: PageType,
  multiPgPageTypes: PageType[],
): boolean => {
  return filterKey === FilterKeys.PRODUCT_GROUP && !multiPgPageTypes.includes(pageToCarryOver) && isMultiPg;
};

export const getCarryOverAllFiltersForApplyV2 = (
  allFilters: SelectedFilters[],
  currentPage: PageType,
): SelectedFilters[] => {
  const clonedAllFilters = cloneDeep(allFilters);
  const currentPageFilters: Filters[] = clonedAllFilters[currentPage].selectedFilters;

  const { isMultiCountry, isMultiProductGroup } = multiCountryProductGroupConfigs[currentPage];
  const multiPgPageTypes: PageType[] = Object.entries(multiCountryProductGroupConfigs)
    .filter(([_, { isMultiProductGroup: isMultiPGs }]) => isMultiPGs)
    .map(([pageType]) => Object.keys(PageType).indexOf(pageType));

  return currentPageFilters
    .reduce((accFinalFilters, currentPageFilter) => {
      const { filterKey } = currentPageFilter;
      const pagesToCarryOver: PageType[] = getCarryOverPagesByFilterSettings(filterKey, currentPage);

      pagesToCarryOver.forEach((pageToCarryOver) => {
        const filtersOfTargetPageToCarryOver = accFinalFilters[pageToCarryOver].selectedFilters;
        if (isMultiCountry && isMultiProductGroup) {
          const isMultiPg = currentPageFilter.options.length > 1;
          const isMultiCountrySelected = currentPageFilter.options.length > 1;
          if (filterKey === FilterKeys.PRODUCT_GROUP) {
            // Multi PG
            // RO: Bring over
            // OR/PA: Show popup modal, aka remove current applied pgs on the target page
            if (shouldRemoveMultiPgFilter(filterKey, isMultiPg, pageToCarryOver, multiPgPageTypes)) {
              removeFilterByKey(filtersOfTargetPageToCarryOver, filterKey);
              return;
            }
          }

          if (
            shouldSkipMultiCountryFilter(
              filterKey,
              isMultiCountrySelected,
              pageToCarryOver,
              multiPgPageTypes,
              accFinalFilters,
            )
          ) {
            return;
          }
        }

        updateFilters(filtersOfTargetPageToCarryOver, currentPageFilter);
      });

      return accFinalFilters;
    }, clonedAllFilters)
    .map((result) => ({
      ...result,
      selectedFilters: sortFilters(result.selectedFilters),
    }));
};

/**
 * Returns a copy of all filters with clear all applied on all carry over pages.
 * @param allSelectedFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param pageTypeAndName
 * @returns Copy of all filters
 */
const getCarryOverClearAllFilters = (
  allSelectedFilters: SelectedFilters[],
  pageTypeAndName: PageTypeAndName,
): SelectedFilters[] => {
  let finalResult = cloneDeep(allSelectedFilters);
  const { pageType } = pageTypeAndName;
  const currentPageBaseFilterKeys: FilterKeys[] = getBaseFilterKeysByPageTypeAndPageName(
    pageTypeAndName,
    finalResult[pageType].selectedFilters,
  );

  // Get current page filters that are not base filters
  const filtersToDelete: Filters[] = finalResult[pageType].selectedFilters.filter(
    (sideMenuFilter) => !currentPageBaseFilterKeys.includes(sideMenuFilter.filterKey),
  );
  filtersToDelete.forEach((filterToDelete) => {
    const pages = getCarryOverPagesByFilterSettings(filterToDelete.filterKey, pageType);
    pages.forEach((page) => {
      finalResult = getRemovedSpecificFilter(finalResult, page, filterToDelete.filterKey);
    });
  });
  return finalResult;
};

/**
 * Returns a copy of carried over all filters for filter delete operations.
 *
 * Includes:
 * 1. clear all filters
 * 2. clear specific filter
 *
 * See: https://jira.gfk.com/browse/NEO-18829
 * @param allSelectedFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param pageTypeAndName Specifies current page
 * @param filterKey Specifies which filterKey to remove filter. Leave undefined to clear all filters
 * @param toRemove Specifies which option to remove, if falsy, remove the filter. Leave undefined to clear all filters
 * @param filterTitle
 * @returns Copy of carried over all filters
 */
export const getCarryOverAllFiltersForDelete = (
  allSelectedFilters: SelectedFilters[],
  pageTypeAndName: PageTypeAndName,
  filterKey?: FilterKeys,
  toRemove?: string,
  filterTitle?: string,
): SelectedFilters[] => {
  let finalResult = cloneDeep(allSelectedFilters);
  const { pageType } = pageTypeAndName;
  if (filterKey === undefined || toRemove === undefined || filterTitle === undefined) {
    return getCarryOverClearAllFilters(finalResult, pageTypeAndName);
  }
  getCarryOverPagesByFilterSettings(filterKey, pageType).forEach((page) => {
    finalResult = getRemovedSpecificFilter(finalResult, page, filterKey, toRemove, filterTitle);
  });
  return finalResult;
};

/*
 * function that checks if filter selection is single or multiple
 */
export const isMulti = (key: FilterKeys, type: PageType, pagesConfig: PagesConfig) => {
  const { settingItem } = getSpecificFilterSettings(key, type);

  // To fix the bug when pg overview is still loading the filter in the side menu, but user has navigated to RS
  if (settingItem) {
    settingItem.isMulti = isEnableMultiCountrySelection(settingItem, pagesConfig);

    return settingItem.isMulti;
  }

  return false;
};

/*
 * function that gets the filters to be displayed in the Select component of the filters
 */
export const getDisplayValue = (
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
  pageType: PageType,
  currentFilter: Filters,
  options: Option[],
) => {
  if ([PageType.RETAILER_OVERVIEW, PageType.CATEGORY_LEADERBOARD].includes(pagesConfig.pageType)) {
    defaultOneOptionForCountry(pagesConfig, updateSideFilters, currentFilter, options, pagesConfig.pageType);
  }

  const filters = [...pagesConfig.selectedFilters[pageType].selectedFilters];
  const deepCopyFilters = cloneDeep(filters);

  const index = deepCopyFilters.findIndex((e) => e.filterKey === currentFilter.filterKey);

  if (index !== -1 && pageType === PageType.RI_PRODUCT && currentFilter.filterKey === FilterKeys.ADDFEAT) {
    const addFeatIndex = filters.findIndex(
      (e) => e.feature === currentFilter.feature && e.filterKey === currentFilter.filterKey,
    );
    const addFeatFilters = deepCopyFilters[addFeatIndex];
    const isCurrentFilterExistFromAddFeatFilters = () => {
      return addFeatFilters.options?.some((ele) => currentFilter.options?.some((ele2) => ele.value === ele2.value));
    };

    if (
      addFeatIndex !== -1 &&
      currentFilter.filterTitle === addFeatFilters.filterTitle &&
      isCurrentFilterExistFromAddFeatFilters()
    ) {
      return deepCopyFilters[addFeatIndex].options;
    }
  }

  return index !== -1 && currentFilter.filterTitle === deepCopyFilters[index].filterTitle
    ? deepCopyFilters[index].options
    : [];
};

/*
 * function that returns the first option in Select component at Country if no selection is done at default
 */
export const defaultOneOptionForCountry = (
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
  selectFilter: Filters,
  options: Option[],
  currentPageType: PageType,
) => {
  if (selectFilter.filterKey !== FilterKeys.COUNTRY) return;

  const appliedFilters = pagesConfig.appliedFilters[currentPageType];
  const hasCountry = appliedFilters.selectedFilters.some((f) => f.filterKey === FilterKeys.COUNTRY);
  if (hasCountry) return;

  const toUpdate: Filters = {
    dataType: selectFilter.filterTitle,
    feature: selectFilter.filterTitle,
    filterTitle: selectFilter.filterTitle,
    filterKey: selectFilter.filterKey,
    options: options.slice(0, 1),
    disabled: false,
  };

  const nextAppliedFilters = pagesConfig.appliedFilters.map((af, i) =>
    i === currentPageType ? { ...af, selectedFilters: [...af.selectedFilters, toUpdate] } : af,
  );

  updateSideFilters({ appliedFilters: nextAppliedFilters });
  pagesConfig.setAppliedFilters(nextAppliedFilters);
  pagesConfig.setSelectedFilters(nextAppliedFilters);
};

export const spliceFilterObjectIfToUpdateIsEmpty = (index: number, toUpdate: Filters, filters: Filters[]) => {
  if (toUpdate.options.length === 0) {
    filters.splice(index, 1);
  }
};

export const findIndexFromFilterWithToUpdate = (filters: Filters[], toUpdate: Filters) => {
  return filters.findIndex((e) => e.filterKey === toUpdate.filterKey && e.feature === toUpdate.feature);
};

export const updateAppliedFilters = (
  pagesConfig: PagesConfig,
  toUpdate: Filters,
  selectedFilters: SelectedFilters[],
) => {
  const pageType: PageType = pagesConfig.pageType;
  const allFilters = [...pagesConfig.appliedFilters];
  const filters = [...allFilters[pageType].selectedFilters];
  const index = filters.findIndex((e) => e.filterKey === toUpdate.filterKey);

  if (index !== -1) {
    filters[index] = toUpdate;
  } else {
    filters.push(toUpdate);
  }

  if (!(pagesConfig.pageName === PageName.TRADING_ASSISTANT && toUpdate.filterKey === FilterKeys.BRAND)) {
    spliceFilterObjectIfToUpdateIsEmpty(index, toUpdate, filters);
  }

  const filterUpdate = { pageType: pagesConfig.pageType, selectedFilters: sortFilters(filters) };
  allFilters[pageType] = filterUpdate;
  pagesConfig.setAppliedFilters(allFilters);

  const isFiltersAligned = isEqual(filterUpdate.selectedFilters, selectedFilters[pageType].selectedFilters);
  pagesConfig.setApplyFilters(!isFiltersAligned);
  pagesConfig.setClearFilters(!isFiltersAligned);

  return allFilters;
};

/*
 * function that updates the selected filters when there are changes in the filter tray
 */
export const updateSelectedFilters = (pagesConfig: PagesConfig, pageType: PageType, toUpdate: Filters) => {
  const allFilters = [...pagesConfig.selectedFilters];
  const filters = [...allFilters[pageType].selectedFilters];
  const index = filters.findIndex((e) => e.filterKey === toUpdate.filterKey);

  if (index !== -1 && pageType === PageType.RI_PRODUCT && toUpdate.filterKey === FilterKeys.ADDFEAT) {
    handleAddFeatFilter(filters, toUpdate);
  } else {
    handleDefaultFilter(filters, toUpdate);
  }

  // checks if price input is an empty string or not. If yes, remove it from the selected filters
  handleEmptyPriceFilter(filters, toUpdate, index);

  const { filterKey } = toUpdate;
  if ([PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW, PageType.TRADING_ASSISTANT].includes(pageType) && filterKey === FilterKeys.COUNTRY) {
    handleExploreCountryFilter(filters, toUpdate);
  }

  return updateFiltersState(pagesConfig, filters, pageType, toUpdate);
};

const handleEmptyPriceFilter = (filters: Filters[], toUpdate: Filters, index: number) => {
  if (
    index !== -1 &&
    (toUpdate.filterKey === FilterKeys.AVERAGE_PRICE_MAX || toUpdate.filterKey === FilterKeys.AVERAGE_PRICE_MIN) &&
    (toUpdate.options[0].value === '0' || toUpdate.options[0].value === '')
  ) {
    filters.splice(index, 1);
  }
};

const handleDefaultFilter = (filters: Filters[], toUpdate: Filters) => {
  const index = filters.findIndex((e) => e.filterKey === toUpdate.filterKey);
  if (index !== -1) {
    filters[index] = toUpdate;
  } else {
    filters.push(toUpdate);
  }
  spliceFilterObjectIfToUpdateIsEmpty(index, toUpdate, filters);
};

export const handleAddFeatFilter = (filters: Filters[], toUpdate: Filters) => {
  const addFeatIndex = findIndexFromFilterWithToUpdate(filters, toUpdate);
  if (addFeatIndex !== -1) {
    filters[addFeatIndex] = toUpdate;
  } else {
    filters.push(toUpdate);
  }
  spliceFilterObjectIfToUpdateIsEmpty(addFeatIndex, toUpdate, filters);
};

const updateFiltersState = (pagesConfig: PagesConfig, filters: Filters[], pageType: PageType, toUpdate: Filters) => {
  const filterUpdate: SelectedFilters = {
    pageType: pagesConfig.pageType,
    selectedFilters: sortFilters(filters),
  };

  const allFilters = [...pagesConfig.selectedFilters];
  allFilters[pageType] = filterUpdate;

  pagesConfig.setSelectedFilters(allFilters);

  if (toUpdate.filterKey !== FilterKeys.PRODUCT_GROUP) {
    const isApplyButtonEnabled = !isEqualWithCaseInsensitive(
      filterUpdate.selectedFilters,
      pagesConfig.appliedFilters[pageType].selectedFilters,
    );
    pagesConfig.setApplyFilters(isApplyButtonEnabled);
    pagesConfig.setClearFilters(isApplyButtonEnabled);
  }

  return allFilters;
};

export const handleExploreCountryFilter = (filters: Filters[], toUpdate: Filters) => {
  if (toUpdate.options.length > 1) {
    const pgFilter = filters.find(byFilterKey(FilterKeys.PRODUCT_GROUP));
    if (!pgFilter) {
      const baseFilters = exploreResponseConfig.response[0].filters;
      const productGroupOptions = baseFilters[baseFilters.length - 1].options;
      filters.push(
        createToUpdateFilter(
          { filterKey: FilterKeys.PRODUCT_GROUP, filterTitle: FilterTitle.PRODUCT_GROUP },
          [productGroupOptions[0]],
          false,
        ),
      );
    } else {
      const { options: pgOptions } = pgFilter;
      if (pgOptions.length === 0 || pgOptions.length > 1) {
        pgOptions.splice(1, pgOptions.length - 1);
      }
    }
  }
};

export const isEqualWithCaseInsensitive = (selectedFilters: Filters[], appliedFilters?: Filters[]) => {
  return (
    (selectedFilters &&
      appliedFilters &&
      JSON.stringify(selectedFilters).toUpperCase() === JSON.stringify(appliedFilters).toUpperCase()) ||
    isEqual(selectedFilters, appliedFilters)
  );
};

export const updateSelectedBrandFilter = (pagesConfig: PagesConfig, pageType: PageType, toUpdate: Filters) => {
  const allFilters = [...pagesConfig.appliedFilters];
  const filters = [...allFilters[pageType].selectedFilters];
  const index = filters.findIndex((e) => e.filterKey === toUpdate.filterKey);

  if (index !== -1) {
    filters[index] = toUpdate;
  } else {
    filters.push(toUpdate);
  }

  const filterUpdate = { pageType: pagesConfig.pageType, selectedFilters: sortFilters(filters) };
  allFilters[pageType] = filterUpdate;
  pagesConfig.setSelectedFilters(allFilters);

  const isFiltersAligned = isEqual(filterUpdate, pagesConfig.appliedFilters[pageType]);
  pagesConfig.setApplyFilters(!isFiltersAligned);
  pagesConfig.setClearFilters(!isFiltersAligned);

  return allFilters;
};

/*
 * function that checks if Brand, Priceband, Channel and Location filters selected
 * ex: if brand is selected, region should have no options
 */
export const checkFourFilterTypesSelected = (
  filterIndex1: number,
  filterIndex2: number,
  filterIndex3: number,
  allowedLength: number,
): boolean => {
  return [filterIndex1, filterIndex2, filterIndex3].filter((e) => e !== -1).length === allowedLength;
};

/**
 * Returns whether the product group updated or not.
 *  if the current product group same as updated product group then return false else true.
 * @param {Filters[]} currentProductGroup The current selected product group.
 * @param {Filters[]} updatedProductGroup The applied or updated product group.
 * @return {boolean} equality of current product group with updated product group.
 **/
export const isProductGroupUpdated = (currentProductGroup: Filters[], updatedProductGroup: Filters[]): boolean => {
  return !isEqual(
    currentProductGroup.find((v) => v.filterKey === FilterKeys.PRODUCT_GROUP)?.options,
    updatedProductGroup.find((v) => v.filterKey === FilterKeys.PRODUCT_GROUP)?.options,
  );
};

/**
 * @param {PagesConfig} pagesConfig The current pages config context.
 * @param {(payload: SideFiltersState) => (dispatch: any) => void} updateSideFilters action creators of update side filters.
 * @param {Filters[]} currentProductGroup The current selected product group.
 * @param {Filters[]} updatedProductGroup The applied or updated product group.
 **/
export const resetFiltersBasedOnSpecificPageType = (
  pagesConfig: PagesConfig,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
  currentProductGroup: Filters[],
  updatedProductGroup: Filters[],
): void => {
  if (
    pagesConfig.pageType === PageType.EXPLORE ||
    pagesConfig.pageType === PageType.RI_PRODUCT ||
    pagesConfig.pageType === PageType.TRADING_ASSISTANT ||
    pagesConfig.pageType === PageType.OPPORTUNITY_REVIEW ||
    pagesConfig.pageType === PageType.PERFORMANCE_ANALYSIS
  ) {
    if (isProductGroupUpdated(currentProductGroup, updatedProductGroup) && checkIsRetailerSame(pagesConfig)) {
      const resetPageTypes: PageType[] = [
        PageType.EXPLORE,
        PageType.RI_PRODUCT,
        PageType.TRADING_ASSISTANT,
        PageType.OPPORTUNITY_REVIEW,
        PageType.PERFORMANCE_ANALYSIS,
      ];
      // exclude current page
      const selectedResetFiltersPageTypes = resetPageTypes.filter((pageType) => pageType !== pagesConfig.pageType);
      // only include the page type which exlude the current page for reset
      const selectedResetPageAppliedFilters = pagesConfig.appliedFilters.filter((filter) =>
        selectedResetFiltersPageTypes.includes(filter.pageType),
      );

      // remove null page type
      const selectedResetFiltersPageTypesWithoutNull = selectedResetPageAppliedFilters.filter(
        (filter) => filter.selectedFilters.length > 0 && filter.selectedFilters.every((e) => e !== null),
      );
      let allFilters: SelectedFilters[] = pagesConfig.selectedFilters;
      // reset all the specific pages that need to reset the filters
      selectedResetFiltersPageTypesWithoutNull.forEach((filter) => {
        allFilters = [...clearAllFilters(pagesConfig.selectedFilters, { pageType: filter.pageType })];
      });
      pagesConfig.setSelectedFilters(allFilters);
      pagesConfig.setAppliedFilters(allFilters);
      pagesConfig.setApplyFilters(false);
      pagesConfig.setClearFilters(true);

      const storeFilterSettings = {
        appliedFilters: allFilters,
      };
      updateSideFilters(storeFilterSettings);
    }
  }
};

/**
 * Return boolean value to enable/disable multi country selection
 *
 * @param settingsItem settings item that store the flag of multi selection for side filters,
 *                     and also carryOverScope which specify which page share same side filters
 * @param pagesConfig PagesConfig
 * **/
export const isEnableMultiCountrySelection = (
  settingsItem: { filterKey: FilterKeys; isMulti: boolean; carryOverScope: FilterCarryOverScope },
  pagesConfig: PagesConfig,
): boolean => {
  if (settingsItem.filterKey !== FilterKeys.COUNTRY) {
    return settingsItem.isMulti;
  }

  const isMultiCountryClient = checkIsMultiCountryClient(pagesConfig.clientId);

  if (isMultiCountryClient) {
    return (
      (pagesConfig.pageName === PageName.PERFORMANCE_ANALYSIS || pagesConfig.pageName === PageName.EXPLORE) &&
      pagesConfig.isMultiCountryDropdown
    );
  }

  return settingsItem.isMulti;
};

/**
 * To check if side filters is able to carry over between OR/PA and Products page
 * @param currentPageType current page type
 * @param pagesConfig PagesConfig
 * @param appliedFiltersCountry applied filters
 *
 * */
const isAbleToCarryORPAExplorePageToFroProductsPage = (
  currentPageType: PageType,
  pagesConfig: PagesConfig,
  appliedFiltersCountry: Filters,
) => {
  //if currently on OR/PA pages
  if ([PageType.OPPORTUNITY_REVIEW, PageType.PERFORMANCE_ANALYSIS, PageType.EXPLORE].includes(currentPageType)) {
    return pagesConfig.hitlistsClientCountries.some(
      (hitlistCountry) => hitlistCountry.id === appliedFiltersCountry.options[0].value,
    );
  }
  //if currently on Products Page, check if its possible to bring over to OR/PA
  return pagesConfig.clientCountries.some((country) => country.id === appliedFiltersCountry.options[0].value);
};

/**
 * To check if side filters is able to carry over between OR/PA and TA page
 * @param currentPageType current page type
 * @param pagesConfig PagesConfig
 * @param appliedFiltersCountry applied filters
 *
 * */
const isAbleToCarryORPAExplorePageToFroTAPage = (
  //should only pass in either PageType.OPPORTUNITY_REVIEW, PageType.PERFORMANCE_ANALYSIS or PageType.TRADING_ASSISTANT
  currentPageType: PageType,
  pagesConfig: PagesConfig,
  appliedFiltersCountry: Filters,
) => {
  //if currently on OR/PA pages
  if ([PageType.OPPORTUNITY_REVIEW, PageType.PERFORMANCE_ANALYSIS, PageType.EXPLORE].includes(currentPageType)) {
    return pagesConfig.marketPremiumClientCountries.some(
      (marketPremiumCountry) => marketPremiumCountry.id === appliedFiltersCountry.options[0].value,
    );
  }
  //if currently on TA Page, check if its possible to bring over to OR/PA
  return pagesConfig.clientCountries.some((country) => country.id === appliedFiltersCountry.options[0].value);
};

/**
 * To check if side filters is able to carry over between Prod and TA page
 * @param currentPageType current page type
 * @param pagesConfig PagesConfig
 * @param appliedFiltersCountry applied filters
 *
 * */
const isAbleToCarryProdPageToFroTAPage = (
  currentPageType: PageType, //should only pass in either PageType.RI_PRODUCT or PageType.TRADING_ASSISTANT
  pagesConfig: PagesConfig,
  appliedFiltersCountry: Filters,
) => {
  //if currently on Products page
  if (PageType.RI_PRODUCT === currentPageType) {
    return pagesConfig.marketPremiumClientCountries.some(
      (marketPremiumCountry) => marketPremiumCountry.id === appliedFiltersCountry.options[0].value,
    );
  }
  //if currently on TA Page, check if its possible to bring over to Products
  return pagesConfig.hitlistsClientCountries.some((country) => country.id === appliedFiltersCountry.options[0].value);
};

export const getExcludedPagesToCarryOver = (pagesConfig: PagesConfig) => {
  const currentPageType = pagesConfig.pageType;
  const appliedFilters = pagesConfig.appliedFilters[currentPageType].selectedFilters;
  const appliedFiltersCountry = [...appliedFilters].find(byFilterKey(FilterKeys.COUNTRY))!;
  const pagesToExclude: PageType[] = [];
  switch (currentPageType) {
    case PageType.OPPORTUNITY_REVIEW:
    case PageType.PERFORMANCE_ANALYSIS:
      if (!isAbleToCarryORPAExplorePageToFroTAPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.TRADING_ASSISTANT);
      }
      if (!isAbleToCarryORPAExplorePageToFroProductsPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.RI_PRODUCT);
      }
      break;
    case PageType.RI_PRODUCT:
      if (!isAbleToCarryORPAExplorePageToFroProductsPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.OPPORTUNITY_REVIEW);
        pagesToExclude.push(PageType.PERFORMANCE_ANALYSIS);
        pagesToExclude.push(PageType.EXPLORE);
      }
      if (!isAbleToCarryProdPageToFroTAPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.TRADING_ASSISTANT);
      }
      break;
    case PageType.TRADING_ASSISTANT:
      if (!isAbleToCarryORPAExplorePageToFroTAPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.OPPORTUNITY_REVIEW);
        pagesToExclude.push(PageType.PERFORMANCE_ANALYSIS);
        pagesToExclude.push(PageType.EXPLORE);
      }
      if (!isAbleToCarryProdPageToFroTAPage(currentPageType, pagesConfig, appliedFiltersCountry)) {
        pagesToExclude.push(PageType.RI_PRODUCT);
      }
      break;
    case PageType.EXPLORE:
      break;
  }
  return pagesToExclude;
};

//Add pages that allow ADDFEAT/SEGFEAT when multi pg is selected
export const isMultiPgCommonFeatureAllowed = (pageType: PageType) =>
  [PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW, PageType.RETAILER_OVERVIEW, PageType.TRADING_ASSISTANT].includes(pageType);

export const removeMultiFeature = (adjustedFilters: SelectedFilters[], isMultiPg: boolean) => {
  for (const filter of adjustedFilters) {
    if (isMultiPg && !isMultiPgCommonFeatureAllowed(filter.pageType)) {
      removeFilterByKey(filter.selectedFilters, FilterKeys.ADDFEAT);
      removeFilterByKey(filter.selectedFilters, FilterKeys.SEGFEAT);
    }
  }
};
